import { useRecoilCallback } from 'recoil'
import { useTranslation } from 'react-i18next'
import type { ApplicationType } from '@messenger/types'
import {
  getEditorPageContent,
  saveEditorPageContent,
} from '@/api/editorPageContent'
import { EDITOR_TEXT_BOX_CONVERSION_VERSION_REASON } from '@/api/orderProducts'
import {
  canvasPageIdAtom,
  itemAtomFamily,
  selectedItemsAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import { caseAtom } from '@/atoms/caseAtoms'
import {
  editorPageContentAtomFamily,
  editorRemintedItemIdsAtom,
} from '@/atoms/editorTextBoxAtoms'
import { applicationTypeAtom } from '@/atoms/globalAtoms'
import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import { useEditorSaveGesture } from '@/hooks/editor/useEditorSaveGesture'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useSetToast } from '@/hooks/utility/useSetToast'
import {
  EditorTextBoxConversionError,
  editorCaseServiceIds,
  editorTextBoxConversionForCaseServices,
  editorTextBoxConversionToastText,
  editorTextBoxFromTinyMceItem,
  editorTextBoxItemGeometry,
  editorTextBoxItemStub,
  type EditorTextBoxConversion,
  type EditorTextBoxTinyMceItem,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { logger, EventCategory } from '@/utils/logger'

/**
 * The item as a box. An item stored with no height paints at its text's
 * height, which only the canvas knows: the frame its load result painted,
 * as the item registered it for the overlay.
 */
const convertTinyMceItem = (
  item: EditorTextBoxTinyMceItem,
  paintedHeight: number,
): EditorTextBoxConversion => {
  try {
    return editorTextBoxFromTinyMceItem(item)
  } catch (error) {
    if (
      error instanceof EditorTextBoxConversionError &&
      error.reason === 'height' &&
      paintedHeight > 0
    )
      return editorTextBoxFromTinyMceItem({ ...item, height: paintedHeight })
    throw error
  }
}

/**
 * Deletes the box a conversion wrote when the conversion cannot finish: left
 * in place, the row would paint over the TinyMCE item on every surface from
 * the next load on. A delete that fails as well is logged under the box's id.
 */
const withdrawConvertedBox = async (
  pageId: string,
  boxId: string,
): Promise<void> => {
  try {
    await saveEditorPageContent(pageId, { boxes: [], deleteBoxIds: [boxId] })
  } catch (error) {
    logger.error({
      message: 'editor_convert_text_box_withdraw_failed',
      category: EventCategory.SAVE,
      data: { pageId, boxId },
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
}

/**
 * Turns the TinyMCE text item `itemId` into a Lotus text box under the same
 * id, for good: the editor has no way back, and the version this takes
 * first is the one an operator restores. It runs as a Save of the line,
 * under the tab's save latch, and refuses before any row is written when no
 * version covers it, when the converter cannot build the box, when the item
 * left the canvas while the version was taken, or when the item's id was
 * minted at this load and the stored page JSON does not hold it. A service
 * block bound to a service row the case does not hold is written unbound.
 * The box is written the way a toolbar insert writes one, and deleted again
 * when anything after that write fails; Save changes then writes the page,
 * whose JSON takes the box's stub.
 */
export const useEditorConvertTextBox = () => {
  const { t, i18n } = useTranslation()
  const syncCurrentPageToEditorPages = useSyncCurrentPageToEditorPages()
  const runSaveGesture = useEditorSaveGesture()
  const handleError = useErrorHandler('useEditorConvertTextBox')
  const setToast = useSetToast()

  // The item as the canvas holds it at the moment of the call, with the
  // frame it paints and the service rows of the case. A snapshot handed to a
  // callback stays as it was when the callback began, and the canvas stays
  // editable while the version is taken.
  const readCanvasItem = useRecoilCallback(
    ({ snapshot }) =>
      (itemId: string) => ({
        item: snapshot.getLoadable(itemAtomFamily(itemId)).valueMaybe(),
        paintedHeight:
          snapshot.getLoadable(itemControlStateAtomFamily(itemId)).valueMaybe()
            ?.position.height ?? 0,
        caseServiceIds: editorCaseServiceIds(
          snapshot.getLoadable(caseAtom).valueMaybe(),
        ),
      }),
    [],
  )

  return useRecoilCallback(
    ({ snapshot, set, reset }) =>
      async (itemId: string): Promise<boolean> => {
        const refuse = (cause: unknown): false => {
          handleError(
            t('editor.textBox.convertFromTinyMce.failed'),
            cause instanceof Error ? cause : new Error(String(cause)),
          )
          return false
        }
        const [pageId, item, reminted] = await Promise.all([
          snapshot.getPromise(canvasPageIdAtom),
          snapshot.getPromise(itemAtomFamily(itemId)),
          snapshot.getPromise(editorRemintedItemIdsAtom),
        ])
        if (!pageId)
          return refuse(new Error('conversion without a canvas page id'))
        if (item?.type !== 'text')
          return refuse(new Error(`item ${itemId} is not a TinyMCE text item`))
        if (reminted[itemId])
          return refuse(
            new Error(
              `item ${itemId} has an id this load minted, which the stored page does not hold`,
            ),
          )
        // The atom is declared in a JavaScript module, so its value arrives
        // untyped.
        const applicationType = (await snapshot.getPromise(
          applicationTypeAtom,
        )) as ApplicationType

        const converted = await runSaveGesture(
          'convert_text_box',
          applicationType,
          async (coveringVersion) => {
            // That version is the conversion's only way back, so without it
            // nothing is written.
            if (coveringVersion.versionNumber === undefined)
              return refuse(
                new Error(
                  `no version covers the conversion of ${itemId}: ${coveringVersion.fallbackCause}`,
                ),
              )
            const current = readCanvasItem(itemId)
            if (current.item?.type !== 'text')
              return refuse(
                new Error(
                  `item ${itemId} left the canvas before its box was written`,
                ),
              )
            let conversion: EditorTextBoxConversion
            try {
              conversion = editorTextBoxConversionForCaseServices(
                convertTinyMceItem(
                  current.item as EditorTextBoxTinyMceItem,
                  current.paintedHeight,
                ),
                current.caseServiceIds,
              )
            } catch (error) {
              return refuse(error)
            }
            let boxWritten = false
            try {
              await saveEditorPageContent(pageId, {
                boxes: [conversion.box],
                deleteBoxIds: [],
              })
              boxWritten = true
              const { data: bundle } = await getEditorPageContent(pageId)
              const row = bundle.boxes.find((box) => box.boxId === itemId)
              if (row === undefined)
                throw new Error(
                  `the rows of page ${pageId} hold no box ${itemId} after its write`,
                )
              if (readCanvasItem(itemId).item?.type !== 'text')
                throw new Error(
                  `item ${itemId} left the canvas while its box was written`,
                )
              // The canvas is synced before the item becomes the box: a set
              // here is not in the sync's snapshot until React commits, so
              // the page JSON takes the box's stub at the next sync, as it
              // does an inserted box's, and the page's hydration paints the
              // row in the item's place meanwhile.
              await syncCurrentPageToEditorPages()
              set(itemAtomFamily(itemId), {
                ...editorTextBoxItemStub(itemId),
                ...editorTextBoxItemGeometry(row),
              })
              reset(itemControlStateAtomFamily(itemId))
              set(editorPageContentAtomFamily(pageId), bundle)
              set(selectedItemsAtom, [itemId])
              set(unsavedChangesAtom, true)
            } catch (error) {
              if (boxWritten) await withdrawConvertedBox(pageId, itemId)
              return refuse(error)
            }
            setToast({
              text: editorTextBoxConversionToastText(
                conversion.report,
                t,
                i18n.resolvedLanguage,
              ),
            })
            logger.info({
              message: 'editor_convert_text_box_success',
              category: EventCategory.INSERT,
              data: {
                pageId,
                boxId: itemId,
                coveringVersionNumber: coveringVersion.versionNumber,
                report: conversion.report,
              },
            })
            return true
          },
          EDITOR_TEXT_BOX_CONVERSION_VERSION_REASON,
        )
        // A conversion that never ran, because another Save holds the line,
        // converted nothing.
        return converted === true
      },
    [
      t,
      i18n,
      syncCurrentPageToEditorPages,
      runSaveGesture,
      handleError,
      setToast,
      readCanvasItem,
    ],
  )
}
